/**
 * @description Hook to manage auto-scrolling behavior for a slider.
 * @param {T[]} items - The items to display in the slider.
 * @param {number} scrollSpeed - The speed at which the slider scrolls.
 * @returns {Object} An object containing the container reference, the duplicated items, the x motion value, and the functions to handle mouse enter and leave.
 */

import {
  useRef, useEffect, useState, useMemo,
} from "react";
import { useMotionValue, useAnimationFrame } from "motion/react";

const DUPLICATION_COUNT = 3;
const DEFAULT_SCROLL_SPEED = 0.5;
const GAP_PX = 16;

const calculateSingleSetWidth = (
  container: HTMLDivElement,
  itemsLength: number,
): number => {
  const children = container.children;
  if (children.length === 0) return 0;

  const firstChild = children[0] as HTMLElement;
  const itemWidth = firstChild.offsetWidth;
  return (itemWidth + GAP_PX) * itemsLength;
};

export const useAutoScrollSlider = <T>(items: T[], scrollSpeed: number = DEFAULT_SCROLL_SPEED) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const itemsLengthRef = useRef(items.length);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const isInitializedRef = useRef(false);

  const duplicatedItems = useMemo(
    () => Array(DUPLICATION_COUNT).fill(items).flat(),
    [items],
  );

  useEffect(() => {
    itemsLengthRef.current = items.length;
    isInitializedRef.current = false;

    if (!containerRef.current) return;

    const initializePosition = () => {
      const singleSetWidth = calculateSingleSetWidth(
        containerRef.current!,
        itemsLengthRef.current,
      );

      if (singleSetWidth > 0) {
        x.set(-singleSetWidth);
        isInitializedRef.current = true;
      }
    };

    const timer = setTimeout(initializePosition, 100);

    return () => clearTimeout(timer);
  }, [items.length, x]);

  useAnimationFrame(() => {
    if (isHovering || isDragging || !containerRef.current || !isInitializedRef.current) return;

    const currentX = x.get();
    const singleSetWidth = calculateSingleSetWidth(
      containerRef.current,
      itemsLengthRef.current,
    );

    if (singleSetWidth === 0) return;

    const newX = currentX - scrollSpeed;

    if (newX <= -singleSetWidth * 2) {
      x.set(newX + singleSetWidth);
    }
    else {
      x.set(newX);
    }
  });

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleDragStart = () => {
    setIsDragging(true);
    dragStartXRef.current = x.get();
  };

  const handleDrag = () => {
    if (!containerRef.current) return;

    const currentX = x.get();
    const singleSetWidth = calculateSingleSetWidth(
      containerRef.current,
      itemsLengthRef.current,
    );

    if (singleSetWidth === 0) return;

    if (currentX > 0) {
      x.jump(currentX - singleSetWidth);
      dragStartXRef.current -= singleSetWidth;
    }
    else if (currentX < -singleSetWidth * (DUPLICATION_COUNT - 1)) {
      x.jump(currentX + singleSetWidth);
      dragStartXRef.current += singleSetWidth;
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return {
    containerRef,
    duplicatedItems,
    x,
    handleMouseEnter,
    handleMouseLeave,
    handleDragStart,
    handleDrag,
    handleDragEnd,
    isDragging,
  };
};
